import { useEffect, useState } from 'react';
import { ActionIcon, Button, Group, Loader, Table, Text, TextInput } from '@mantine/core';
import { notifications } from '@mantine/notifications';
import { IconDeviceFloppy, IconPlus, IconTrash } from '@tabler/icons-react';
import { doc, setDoc } from 'firebase/firestore';

import type { EtfIdentifiers as EtfIdentifiersMap } from '../../types';
import { useFirestore } from '../../helpers/firebaseContext';
import useFirestoreDoc from '../../helpers/useFirestoreDoc';
import { isEtfSectionError, useEtfContext } from './etf_common';

type Row = { ticker: string; name: string; isin: string; sedol: string };

//ISIN/SEDOL per ticker live in config/etf_identifiers; the PCF uses the securities doc first and this as fallback.
//Loaded holdings are pre-listed so only the blanks need filling.
const EtfIdentifiers = () => {
    const { data } = useEtfContext();
    const db = useFirestore();
    const configDoc = doc(db, 'config', 'etf_identifiers');
    const { data: config, loading } = useFirestoreDoc<{ securities?: EtfIdentifiersMap }>(configDoc);

    const [rows, setRows] = useState<Row[]>([]);
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        const saved = config?.securities ?? {};
        const fromConfig: Row[] = Object.entries(saved).map(([ticker, v]) => ({ ticker, name: v.name ?? '', isin: v.isin ?? '', sedol: v.sedol ?? '' }));
        const holdings = data && !isEtfSectionError(data.holdings) ? data.holdings.holdings.filter((h) => !h.cash) : [];
        const fromHoldings: Row[] = holdings
            .filter((h) => !saved[h.ticker] && !saved[h.security])
            .map((h) => ({ ticker: h.ticker || h.security, name: h.name, isin: '', sedol: '' }));
        setRows([...fromConfig, ...fromHoldings].sort((a, b) => a.ticker.localeCompare(b.ticker)));
    }, [config, data]);

    const update = (i: number, field: keyof Row, value: string) =>
        setRows((r) => r.map((row, idx) => (idx === i ? { ...row, [field]: value } : row)));

    const save = async () => {
        const securities: EtfIdentifiersMap = {};
        for (const row of rows) {
            const ticker = row.ticker.trim().toUpperCase();
            if (!ticker) continue;
            securities[ticker] = { name: row.name.trim(), isin: row.isin.trim().toUpperCase(), sedol: row.sedol.trim().toUpperCase() };
        }
        setSaving(true);
        try {
            //full replace so deleted rows go away
            await setDoc(configDoc, { securities });
            notifications.show({ color: 'teal', title: 'Identifiers saved', message: `${Object.keys(securities).length} securities in config/etf_identifiers` });
        } catch (e) {
            notifications.show({ color: 'red', title: 'Save failed', message: e instanceof Error ? e.message : String(e) });
        }
        setSaving(false);
    };

    if (loading && !config) return <Loader />;

    return (
        <>
            <Group justify="space-between" mb="xs">
                <Text size="sm" c="dimmed">
                    ISIN / SEDOL per ticker. The securities document wins when it carries them; these fill the gaps until Geneva syncs them.
                    {data ? ' Tickers from the current holdings are pre-listed.' : ''}
                </Text>
                <Group>
                    <Button variant="light" leftSection={<IconPlus size={16} />} onClick={() => setRows((r) => [...r, { ticker: '', name: '', isin: '', sedol: '' }])}>
                        Add row
                    </Button>
                    <Button leftSection={<IconDeviceFloppy size={16} />} loading={saving} onClick={save}>Save</Button>
                </Group>
            </Group>
            <Table withTableBorder>
                <Table.Thead>
                    <Table.Tr>
                        <Table.Th>Ticker</Table.Th>
                        <Table.Th>Name</Table.Th>
                        <Table.Th>ISIN</Table.Th>
                        <Table.Th>SEDOL</Table.Th>
                        <Table.Th />
                    </Table.Tr>
                </Table.Thead>
                <Table.Tbody>
                    {rows.map((row, i) => (
                        <Table.Tr key={i}>
                            <Table.Td><TextInput value={row.ticker} onChange={(e) => update(i, 'ticker', e.currentTarget.value)} placeholder="KFH" /></Table.Td>
                            <Table.Td><TextInput value={row.name} onChange={(e) => update(i, 'name', e.currentTarget.value)} /></Table.Td>
                            <Table.Td><TextInput value={row.isin} onChange={(e) => update(i, 'isin', e.currentTarget.value)} placeholder="KW0EQ…" error={!row.isin && 'missing'} /></Table.Td>
                            <Table.Td><TextInput value={row.sedol} onChange={(e) => update(i, 'sedol', e.currentTarget.value)} error={!row.sedol && 'missing'} /></Table.Td>
                            <Table.Td>
                                <ActionIcon color="red" variant="subtle" onClick={() => setRows((r) => r.filter((_, idx) => idx !== i))}><IconTrash size={16} /></ActionIcon>
                            </Table.Td>
                        </Table.Tr>
                    ))}
                </Table.Tbody>
            </Table>
        </>
    );
};

export default EtfIdentifiers;
